// Connect Google hand-off for a popup that lost window.opener.
//
// Account Settings opens the Google flow in a popup to PROVE a second account.
// /socialAuth normally posts the proof back through window.opener. A page in
// the sign-in that sends an enforced Cross-Origin-Opener-Policy (an
// institution's sign-in, for example) severs that link: the popup has no
// opener, and Settings sees popup.closed === true while it is still open.
//
// Settings therefore marks its popup with a per-attempt nonce. window.open
// copies the opener tab's sessionStorage into the popup at open time, and the
// mark is removed from the Settings tab right after, so only the popup carries
// it. /socialAuth, finding the mark, never signs in with the token. It posts
// the proof on a same-origin BroadcastChannel, and the Settings tab accepts
// only its own nonce. Verified in Chromium, Firefox and WebKit, 2026-10-16:
// the copy is taken at window.open, and the channel and window.close() work
// in a popup severed by COOP.
export const GOOGLE_CONNECT_ATTEMPT_KEY = 'googleConnectAttempt';
export const GOOGLE_CONNECT_CHANNEL_NAME = 'qlearn-google-connect';
export const GOOGLE_CONNECT_PROOF = 'google-connect-proof';
export const GOOGLE_CONNECT_ACK = 'google-connect-ack';

// The Settings popup's own timeout: past it, nobody is listening.
export const GOOGLE_CONNECT_ATTEMPT_MAX_AGE_MS = 10 * 60 * 1000;

const newNonce = () => {
    // No crypto: the popup is still marked, so it never signs in, but there is
    // no hand-off.
    if (typeof window.crypto?.getRandomValues !== 'function') return '';
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const clearGoogleConnectAttempt = () => {
    try {
        window.sessionStorage.removeItem(GOOGLE_CONNECT_ATTEMPT_KEY);
    } catch (e) {
        // Storage blocked: nothing was stored to clear.
    }
};

// Calls open() with this attempt's mark in place, then removes it from this
// tab. Returns { popup, nonce }; nonce is '' when no hand-off is possible.
export const openWithGoogleConnectAttempt = (open) => {
    let nonce = newNonce();
    try {
        window.sessionStorage.setItem(GOOGLE_CONNECT_ATTEMPT_KEY, JSON.stringify({ nonce, at: Date.now() }));
    } catch (e) {
        nonce = '';
    }
    try {
        return { popup: open(), nonce };
    } finally {
        clearGoogleConnectAttempt();
    }
};

// Read and consume: { nonce, fresh } for any well-formed mark, else null.
// Any connect mark means this popup never signs in; only a fresh one is
// worth handing off, because Settings stops listening after the timeout.
export const takeGoogleConnectAttempt = () => {
    let mark;
    try {
        mark = JSON.parse(window.sessionStorage.getItem(GOOGLE_CONNECT_ATTEMPT_KEY));
    } catch (e) {
        mark = null;
    }
    clearGoogleConnectAttempt();
    if (typeof mark?.nonce !== 'string' || typeof mark.at !== 'number' || !Number.isFinite(mark.at)) return null;
    const age = Date.now() - mark.at;
    return { nonce: mark.nonce, fresh: age >= 0 && age < GOOGLE_CONNECT_ATTEMPT_MAX_AGE_MS };
};

export const openGoogleConnectChannel = () => {
    try {
        return typeof window.BroadcastChannel === 'function'
            ? new window.BroadcastChannel(GOOGLE_CONNECT_CHANNEL_NAME)
            : null;
    } catch (e) {
        return null;
    }
};
